/**
 * @file machine_app_view.js
 * @author MachineApps Team
 *
 * @description Creates a view for the machine app type provided. This is used by 'gui_pane_machineapp.js' in assembler,
 * and by the 'MachineApp' page on the pendant. The pendant version is temporary.
 */

import $ from 'jQuery';
import { machineAppItemEditor } from '../machine_app_item_editor';
import { showConfirmationModal } from '../components/machine_app_confirmation_modal';
import { filterList } from '../components/machine_app_filter_list';
import { showMessageBox } from '../components/machine_app_toast_message_box';

/**
 * @typedef {Object} ItemViewProps
 * @property {machineAppLibrary} machineAppLibrary
 * @property {Function} onUpdate
 * @property {Function} createNewItem
 * @property {Function} removeItem
 * @property {Function} listItems
 * @property {Object} flavorText
 * @property {Function} getPreview
 * @property {Function} isItemValidated
 * @property {import('./machine_app_view').MachineAppViewString} view
 * @property {String} assetType

/**
 * Responsible for generating the list of available items (configuration or globalAsset).
 * @param {ItemViewProps} ItemListViewProps
 * @returns {{body: JQuery, header: JQuery}}
 */
export function getItemListView({
  machineAppLibrary,
  onUpdate,
  createNewItem,
  removeItem,
  listItems,
  flavorText,
  getPreview,
  isItemValidated,
  view,
  assetType
}) {

  // Button callbacks
  function lNewItem() {
    const pNewItem = createNewItem(flavorText.defaultName + ' #' + (listItems(assetType).length + 1), assetType);
    lEdit(pNewItem);
  }
  function lEdit(pItem) {
    machineAppItemEditor(pItem, onUpdate);
  }
  function lDuplicate(pItem) {
    function lOnSuccess(pNewItem) {
      lEdit(pNewItem);
      onUpdate();
    }
    function lOnError() {
      showMessageBox(`Failed to duplicate a ${flavorText.singularName.toLowerCase()}`, 'error');
    }
    machineAppLibrary.duplicateItem(pItem, lOnSuccess, lOnError);
  }
  function lDelete(pItem) {
    function lOnSuccess() {
      onUpdate();
    }
    function lOnError() {
      showMessageBox(`Failed to delete a ${flavorText.singularName.toLowerCase()}`, 'error');
    }

    removeItem(pItem.getUuid(), lOnSuccess, lOnError);
  }

  // Tooltip button
  function lCreatePreviewTooltip(pItem) {
    if (!getPreview) {
      return undefined;
    }

    function lPopUpTooltip(pEvent){
      pEvent.stopPropagation();

      // Remove previous tooltip if exists
      $('.machineapp_list_item_preview').remove();

      // Tooltip itself
      const lTooltipContainer = $('<div>')
        .addClass('machineapp_list_item_preview')
        .css({
          top: (pEvent.pageY + 10) + 'px',
          left: pEvent.pageX + 'px'
        })
        .on('click', function(pEvent) {
          pEvent.stopPropagation();
        });


      // Function on document to remove tooltip on click
      const removeTooltipFunc = function(pEvent) {
        if(pEvent.clientX == undefined || pEvent.clientY == undefined) return; // So we safe
        if(lTooltipContainer) {
          lTooltipContainer.remove();
        }
        $(document).off('click', removeTooltipFunc);
      };

      $('body').append(
        lTooltipContainer.append(
          getPreview(pItem)
        )
      );

      $(document).on('click', removeTooltipFunc);
    }

    const lTooltipButton = $('<button>')
      .addClass('machine_app_list_item_button machineapps_preview_button')
      .text('Preview')
      .on('click', lPopUpTooltip);

    return lTooltipButton;
  }

  // Render list elements
  function lCreateItemList(listFilter) {
    const lContainer = $('<div>').addClass('machine_app_item_list_container');
    const lAddItemContainer = $('<div>').addClass('machine_app_item_list_add_container');
    const lList = $('<ul>').addClass('machine_app_item_list ml_scrollable');
    const lAddElementButton = $('<button>')
      .append(
        $('<i>').addClass('far fa-plus'),
        $('<span>').text(flavorText.createButton)
      )
      .on('click', lNewItem);

    let lItems = listItems(assetType);

    if (listFilter) {
      lItems = lItems.filter(pItem => pItem.getName().toUpperCase().includes(listFilter.toUpperCase()));
    }

    // Create and append all config items to list
    lItems.forEach(function(pItem) {
      const lItemNameGroup = $('<div>').addClass('machine_app_item_list_item_label_group');

      // Building the configuration name and maybe the tooltip if applicable
      lItemNameGroup.append($('<label>').text(pItem.getName()));

      if(!isItemValidated(pItem)){
        lItemNameGroup.append($('<label>').text('(Incomplete)').addClass('incomplete_label'));
      }

      // UI Parts
      const lListItem = $('<li>').addClass('machine_app_item_list_item');
      const lButtonGroup = $('<div>');
      const lPreviewTooltip = lCreatePreviewTooltip(pItem);
      const lEditButton = $('<button>')
        .addClass('machine_app_list_item_button')
        .text('Edit')
        .on('click', function() {
          $(this).blur();
          lEdit(pItem);
        });
      const lDuplicateButton = $('<button>')
        .addClass('machine_app_list_item_button duplicate_button')
        .text('Duplicate')
        .on('click', function() { lDuplicate(pItem); });
      const lDeleteButton = $('<button>')
        .addClass('machine_app_list_item_button delete_button')
        .append($('<i>').addClass('icon-delete'))
        .on('click', function() {
          showConfirmationModal({
            pTitle: 'Are you sure that you want to delete?',
            pSubTitle: `Are you sure you want to delete this ${flavorText.singularName}?`,
            pOnCancel: function() { },
            pOnConfirm: function() { lDelete(pItem); },
            pCancelText: 'Cancel',
            pConfirmText: 'Delete'
          });
        });

      // UI
      lList.append(
        lListItem.append(
          lItemNameGroup,
          lButtonGroup.append(
            lPreviewTooltip,
            lEditButton,
            lDuplicateButton,
            lDeleteButton
          )
        )
      );
    });

    return lContainer.append(
      lList,
      lAddItemContainer.append(
        lAddElementButton
      ));
  }

  // Building actual UI
  /////////////////////////////////
  const lHeaderContainer = $('<div>')
    .addClass('machineapp_editor_header_container')
    .append(
      $('<h5>').text(flavorText.pluralName),
      filterList(lRenderList, flavorText.pluralName.toLowerCase())
    );

  const lBodyContainer = $('<div>')
    .addClass('machineapp_editor_body_container');

  function lRenderList(listFilter) {
    lBodyContainer.empty().append(
      lCreateItemList(listFilter)
    );
  }

  lRenderList();

  return {
    body: lBodyContainer,
    header: lHeaderContainer
  };
}

